<template>
  <BlokkliProvider
    v-if="hasNewNavigation"
    v-bind="blokkliProps"
    :edit-path
    class="tablet:sticky tablet:top-0 tablet:z-400 page-menu tablet:w-full tablet:bg-background"
    :class="{
      'h-[1000px]': isEditing,
    }"
  >
    <Transition :name="isMobile ? 'reveal-from-bottom' : ''">
      <div
        v-show="hasActiveMenu"
        class="page-menu-wrapper fixed top-0 left-0 w-full h-screen z-400 bg-background tablet:relative tablet:!block tablet:h-auto tablet:z-600 overflow-auto overscroll-none tablet:overflow-visible pb-[100px] tablet:pb-0"
      >
        <div class="container">
          <div
            v-if="hasChannels || hasLanguages"
            class="page-menu-top flex justify-between items-center border-b border-b-border transition duration-500 tablet:opacity-100"
            :class="{
              'opacity-0': active !== 'menu',
            }"
          >
            <MenuChannels v-if="hasChannels" :channels :active-channel />
            <LanguageSwitcher v-if="hasLanguages" />
          </div>

          <MenuPopup v-if="searchEnabled" :visible="active === 'search'">
            <MenuSearch :visible="active === 'search'" />
          </MenuPopup>

          <MenuPopup :visible="active === 'chat'" class="overflow-hidden pb-3">
            <MenuChat />
          </MenuPopup>
        </div>

        <MenuNavigation
          :visible="menuVisible"
          :quick-links="filterNullArray(quickLinks)"
          :teasers="filterNullArray(teasers)"
          :links
          @close="close"
        />
      </div>
    </Transition>

    <div
      class="page-menu-bottom static container flex items-center tablet:justify-between"
    >
      <Logo v-bind="logo" class="relative z-100" @click="close" />

      <div
        class="page-menu-buttons flex gap-3 px-2 tablet:px-0 tablet:gap-2 bg-primary rounded-md shadow-xl tablet:bg-transparent tablet:!p-0 tablet:rounded-none tablet:shadow-none"
      >
        <MenuButton
          id="chat"
          v-model="active"
          icon="chat"
          :title="$texts('aiChat', 'AI Guide')"
        />
        <MenuButton
          v-if="searchEnabled"
          id="search"
          v-model="active"
          icon="search-2"
          :title="$texts('search', 'Search')"
        />
        <MenuButton
          id="menu"
          v-model="active"
          icon="menu"
          :title="$texts('menu', 'Menu')"
        />
      </div>
    </div>

    <Teleport to="body">
      <Transition name="fade-absolute">
        <div
          v-if="!isEditing && (menuVisible || (hasActiveMenu && isMobile))"
          class="fixed top-0 left-0 w-screen h-screen bg-black/80 z-300 backdrop-blur-2xl"
          @click="close"
        />
      </Transition>
    </Teleport>
  </BlokkliProvider>

  <StorageMenuOldNavigation v-else :links />
</template>

<script setup lang="ts">
import type {
  MenuLinkTreeFirstFragment,
  StorageMenuFragment,
} from '#graphql-operations'
import MenuButton from './Button/index.vue'
import MenuSearch from './Search/index.vue'
import MenuChat from './Chat/index.vue'
import MenuPopup from './Popup/index.vue'
import MenuNavigation from './Navigation/index.vue'
import MenuChannels from './Channels/index.vue'
import { DomainId } from '#graphql-operations'
import { filterNullArray } from '~/helpers'

const props = defineProps<StorageMenuFragment & { isEditing?: boolean }>()

const {
  availableChannels: channels,
  activeChannel,
  isChannelLandingPage,
  availableLanguages,
  activeDomain,
} = useSiteContext()
const { $texts } = useEasyTexts()
const { logo } = useLogo()
const { isMobile } = useViewport()
const searchEnabled = useFeatureFlag('global_search', true)

// The currently active menu type.
const active = ref<'chat' | 'search' | 'menu' | ''>('')

const hasChannels = computed(() => channels.value.length > 1)
const hasLanguages = availableLanguages.length > 1
const menuVisible = computed(() => active.value === 'menu' || props.isEditing)
const hasActiveMenu = computed(() => !!active.value)
const links = computed<MenuLinkTreeFirstFragment[]>(
  () => props.menu?.links || [],
)

function close() {
  active.value = ''
}

onRouteChange((newRoute) => {
  // Don't close the menu when the user clicked on the link to switch the channel.
  if (isChannelLandingPage(newRoute.path)) {
    return
  }

  close()
})

// The path where blökkli editing happens.
// While the menu is visible on every page, editing is only possible
// on the /storage/:id route.
const editPath = computed(() => props.url?.path)

const hasNewNavigation = computed<boolean>(() => {
  return activeDomain === DomainId.zuerich_com
})

const htmlClasses = computed(() => {
  const classes = []
  if (hasChannels.value || hasLanguages) {
    classes.push('has-top-menu')
  }
  if (active.value === 'search') {
    classes.push('has-search-open')
  }
  if (!hasNewNavigation.value) {
    classes.push('has-old-navigation')
  }

  return classes.join(' ')
})

useHead({
  htmlAttrs: {
    class: htmlClasses,
  },
})
</script>

<style lang="postcss">
html {
  --page-menu-top: 0px;
  --page-menu-bottom: 70px;
}

html.has-top-menu {
  --page-menu-top: 60px;
}
html.has-search-open {
  scroll-padding-top: 0;
}

.page-menu-bottom {
  height: var(--page-menu-bottom);
  @screen tablet {
    /* The border below the header. This has to be a pseudo element due to z-index. */
    &:before {
      content: '';
      @apply absolute bottom-0 left-0 pointer-events-none;
      @apply h-[1px] w-full block border-t border-t-border;
    }
  }
}

.page-menu-buttons {
  height: var(--page-menu-bottom);
  @apply fixed bottom-4 left-1/2 -translate-x-1/2 z-500;

  @screen tablet {
    @apply static translate-x-0 left-auto bottom-auto;
  }
}

.page-menu-top {
  height: var(--page-menu-top);
}

.page-menu-bottom {
  height: var(--page-menu-bottom);
}
</style>
